// ============================================================================
// REACT QUERY HOOKS FOR SMART DOCS (Project-Scoped Artifacts)
// Replaces localStorage-based documentStore with backend artifact API
// ============================================================================

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { artifactsApi } from '@/lib/api/client'
import { useSessionStore } from '@/stores/sessionStore'
import type { JSONContent } from '@tiptap/react'
import type {
  Artifact,
  DocContent,
  ArtifactDomain,
  DetectedEntity as SharedDetectedEntity,
  ConfirmedEntity as SharedConfirmedEntity,
} from '@ispot/shared'
import type { SmartDocument, DetectedEntity, ConfirmedEntity, EntityStatus } from '@/types/document'

// ============================================================================
// ADAPTER FUNCTIONS
// Convert between Artifact API and SmartDocument types for component compatibility
// ============================================================================

function artifactToSmartDocument(artifact: Artifact): SmartDocument {
  const content = artifact.content as DocContent

  // Convert shared types to local types
  const detectedEntities: DetectedEntity[] = (content.detectedEntities || []).map((e: SharedDetectedEntity) => ({
    id: e.id,
    type: e.type as DetectedEntity['type'],
    content: e.content,
    evidence: e.evidence,
    confidence: e.confidence,
    status: e.status as EntityStatus,
    suggestedOntologyType: e.suggestedOntologyType,
  }))

  const confirmedEntities: ConfirmedEntity[] = (content.confirmedEntities || []).map((e: SharedConfirmedEntity) => ({
    id: e.id,
    entityId: e.entityId,
    ontologyUri: e.ontologyUri,
    ontologyType: e.ontologyType,
    label: e.label,
    sourceEntityId: e.entityId,
    createdAt: e.createdAt,
    createdFrom: 'detection' as const,
  }))

  return {
    id: artifact.id,
    title: artifact.title,
    content: content.tiptap as JSONContent | null,
    plainText: content.plainText || '',
    detectedEntities,
    confirmedEntities,
    createdAt: artifact.createdAt,
    updatedAt: artifact.updatedAt,
    templateId: artifact.templateId ?? undefined,
    projectId: artifact.projectId,
  }
}

function smartDocumentToDocContent(doc: Partial<SmartDocument>): DocContent {
  return {
    tiptap: doc.content as Record<string, unknown> | null,
    plainText: doc.plainText || '',
    detectedEntities: doc.detectedEntities?.map(e => ({
      id: e.id,
      type: e.type,
      content: e.content,
      evidence: e.evidence,
      confidence: e.confidence,
      status: e.status,
      suggestedOntologyType: e.suggestedOntologyType,
    })),
    confirmedEntities: doc.confirmedEntities?.map(e => ({
      id: e.id,
      entityId: e.entityId,
      ontologyUri: e.ontologyUri,
      ontologyType: e.ontologyType,
      label: e.label,
      createdAt: e.createdAt,
    })),
  }
}

// ============================================================================
// CONTEXT HOOK
// ============================================================================

function useContext() {
  const { accountId, projectId, isReady } = useSessionStore()
  return { accountId, projectId, hasContext: isReady }
}

// ============================================================================
// QUERY HOOKS
// ============================================================================

/**
 * Get all Smart Docs for the current project
 */
export function useSmartDocs() {
  const { accountId, projectId, hasContext } = useContext()

  return useQuery({
    queryKey: ['smartDocs', accountId, projectId],
    queryFn: async () => {
      if (!accountId || !projectId) return []

      const { artifacts } = await artifactsApi.list(accountId, projectId, {
        type: 'Doc',
      })

      // Filter out uploaded/analyzed documents (those belong in Sources → Docs)
      // Smart Docs are user-authored documents without file uploads or entity extraction
      // Also filter out documents that have been through analysis (analyzedAt set) even if graphUri isn't set
      const smartDocsOnly = artifacts.filter(artifact =>
        !artifact.fileName && !artifact.graphUri && !artifact.analyzedAt
      )

      return smartDocsOnly.map(artifactToSmartDocument)
    },
    staleTime: 30000, // 30 seconds
    enabled: hasContext,
  })
}

/**
 * Get a single Smart Doc by ID
 */
export function useSmartDoc(docId: string | null) {
  const { accountId, projectId, hasContext } = useContext()

  return useQuery({
    queryKey: ['smartDoc', accountId, projectId, docId],
    queryFn: async () => {
      if (!docId || !accountId || !projectId) return null

      const { artifact } = await artifactsApi.get(accountId, projectId, docId)
      return artifactToSmartDocument(artifact)
    },
    enabled: hasContext && !!docId,
  })
}

// ============================================================================
// MUTATION HOOKS
// ============================================================================

/**
 * Create a new Smart Doc
 */
export function useCreateSmartDoc() {
  const queryClient = useQueryClient()
  const { accountId, projectId } = useContext()

  return useMutation({
    mutationFn: async ({
      title,
      content,
      plainText,
      templateId,
      domain = 'Market',
      subType = 'Note',
    }: {
      title: string
      content?: JSONContent | null
      plainText?: string
      templateId?: string
      domain?: ArtifactDomain
      subType?: string
    }) => {
      if (!accountId || !projectId) throw new Error('No context')

      const docContent: DocContent = {
        tiptap: content as Record<string, unknown> | null,
        plainText: plainText || '',
      }

      const { artifact } = await artifactsApi.create(accountId, projectId, {
        type: 'Doc',
        subType,
        domain,
        title,
        templateId,
        content: docContent,
        status: 'active',
      })

      return artifactToSmartDocument(artifact)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['smartDocs'] })
    },
  })
}

/**
 * Update a Smart Doc's metadata (title, description)
 */
export function useUpdateSmartDoc() {
  const queryClient = useQueryClient()
  const { accountId, projectId } = useContext()

  return useMutation({
    mutationFn: async ({
      id,
      title,
      description,
    }: {
      id: string
      title?: string
      description?: string
    }) => {
      if (!accountId || !projectId) throw new Error('No context')

      const { artifact } = await artifactsApi.update(accountId, projectId, id, {
        title,
        description,
      })

      return artifactToSmartDocument(artifact)
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['smartDoc', accountId, projectId, id] })
      queryClient.invalidateQueries({ queryKey: ['smartDocs'] })
    },
  })
}

/**
 * Update a Smart Doc's content (TipTap content + plain text)
 */
export function useUpdateSmartDocContent() {
  const queryClient = useQueryClient()
  const { accountId, projectId } = useContext()

  return useMutation({
    mutationFn: async ({
      id,
      content,
      plainText,
      detectedEntities,
      confirmedEntities,
    }: {
      id: string
      content?: JSONContent | null
      plainText?: string
      detectedEntities?: DetectedEntity[]
      confirmedEntities?: ConfirmedEntity[]
    }) => {
      if (!accountId || !projectId) throw new Error('No context')

      // Get current content to merge
      const { artifact } = await artifactsApi.get(accountId, projectId, id)
      const currentContent = artifact.content as DocContent

      const updatedContent: DocContent = {
        tiptap: content !== undefined ? (content as Record<string, unknown> | null) : currentContent.tiptap,
        plainText: plainText !== undefined ? plainText : currentContent.plainText,
        detectedEntities: detectedEntities !== undefined
          ? detectedEntities.map(e => ({
              id: e.id,
              type: e.type,
              content: e.content,
              evidence: e.evidence,
              confidence: e.confidence,
              status: e.status,
              suggestedOntologyType: e.suggestedOntologyType,
            }))
          : currentContent.detectedEntities,
        confirmedEntities: confirmedEntities !== undefined
          ? confirmedEntities.map(e => ({
              id: e.id,
              entityId: e.entityId,
              ontologyUri: e.ontologyUri,
              ontologyType: e.ontologyType,
              label: e.label,
              createdAt: e.createdAt,
            }))
          : currentContent.confirmedEntities,
      }

      const { artifact: updated } = await artifactsApi.update(accountId, projectId, id, {
        content: updatedContent,
      })

      return artifactToSmartDocument(updated)
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['smartDoc', accountId, projectId, id] })
      queryClient.invalidateQueries({ queryKey: ['smartDocs'] })
    },
  })
}

/**
 * Delete (archive) a Smart Doc
 */
export function useDeleteSmartDoc() {
  const queryClient = useQueryClient()
  const { accountId, projectId } = useContext()

  return useMutation({
    mutationFn: async (id: string) => {
      if (!accountId || !projectId) throw new Error('No context')
      await artifactsApi.archive(accountId, projectId, id)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['smartDocs'] })
    },
  })
}

// ============================================================================
// ENTITY MUTATION HOOKS
// ============================================================================

/**
 * Update entity status (accept/reject)
 */
export function useUpdateEntityStatus() {
  const queryClient = useQueryClient()
  const { accountId, projectId } = useContext()

  return useMutation({
    mutationFn: async ({
      docId,
      entityId,
      status,
    }: {
      docId: string
      entityId: string
      status: EntityStatus
    }) => {
      if (!accountId || !projectId) throw new Error('No context')

      // Get current document
      const { artifact } = await artifactsApi.get(accountId, projectId, docId)
      const content = artifact.content as DocContent

      // Update entity status
      const updatedEntities = (content.detectedEntities || []).map(e =>
        e.id === entityId ? { ...e, status } : e
      )

      const updatedContent: DocContent = {
        ...content,
        detectedEntities: updatedEntities,
      }

      await artifactsApi.update(accountId, projectId, docId, {
        content: updatedContent,
      })
    },
    onSuccess: (_, { docId }) => {
      queryClient.invalidateQueries({ queryKey: ['smartDoc', accountId, projectId, docId] })
      queryClient.invalidateQueries({ queryKey: ['smartDocs'] })
    },
  })
}

/**
 * Accept all pending entities
 */
export function useAcceptAllEntities() {
  const queryClient = useQueryClient()
  const { accountId, projectId } = useContext()

  return useMutation({
    mutationFn: async (docId: string) => {
      if (!accountId || !projectId) throw new Error('No context')

      const { artifact } = await artifactsApi.get(accountId, projectId, docId)
      const content = artifact.content as DocContent

      const updatedEntities = (content.detectedEntities || []).map(e =>
        e.status === 'pending' ? { ...e, status: 'accepted' as const } : e
      )

      const updatedContent: DocContent = {
        ...content,
        detectedEntities: updatedEntities,
      }

      await artifactsApi.update(accountId, projectId, docId, {
        content: updatedContent,
      })
    },
    onSuccess: (_, docId) => {
      queryClient.invalidateQueries({ queryKey: ['smartDoc', accountId, projectId, docId] })
      queryClient.invalidateQueries({ queryKey: ['smartDocs'] })
    },
  })
}

/**
 * Reject all pending entities
 */
export function useRejectAllEntities() {
  const queryClient = useQueryClient()
  const { accountId, projectId } = useContext()

  return useMutation({
    mutationFn: async (docId: string) => {
      if (!accountId || !projectId) throw new Error('No context')

      const { artifact } = await artifactsApi.get(accountId, projectId, docId)
      const content = artifact.content as DocContent

      const updatedEntities = (content.detectedEntities || []).map(e =>
        e.status === 'pending' ? { ...e, status: 'rejected' as const } : e
      )

      const updatedContent: DocContent = {
        ...content,
        detectedEntities: updatedEntities,
      }

      await artifactsApi.update(accountId, projectId, docId, {
        content: updatedContent,
      })
    },
    onSuccess: (_, docId) => {
      queryClient.invalidateQueries({ queryKey: ['smartDoc', accountId, projectId, docId] })
      queryClient.invalidateQueries({ queryKey: ['smartDocs'] })
    },
  })
}

// ============================================================================
// MIGRATION HOOKS
// ============================================================================

const LOCAL_STORAGE_KEY = 'intellispot-smart-docs'

interface LocalStorageDoc {
  id: string
  title: string
  content: JSONContent | null
  plainText: string
  detectedEntities: DetectedEntity[]
  confirmedEntities: ConfirmedEntity[]
  createdAt: string
  updatedAt: string
  templateId?: string
}

/**
 * Migrate documents from localStorage to backend
 * Returns migration status
 */
export function useMigrateLocalDocs() {
  const queryClient = useQueryClient()
  const { accountId, projectId, hasContext } = useContext()

  return useMutation({
    mutationFn: async () => {
      if (!accountId || !projectId) throw new Error('No context')

      // Check localStorage
      const stored = localStorage.getItem(LOCAL_STORAGE_KEY)
      if (!stored) return { migrated: 0, total: 0, skipped: 0 }

      let state: { documents?: LocalStorageDoc[] }
      try {
        const parsed = JSON.parse(stored)
        state = parsed.state || {}
      } catch {
        return { migrated: 0, total: 0, skipped: 0, error: 'Invalid localStorage data' }
      }

      const localDocs = state.documents || []
      if (localDocs.length === 0) return { migrated: 0, total: 0, skipped: 0 }

      let migrated = 0
      let skipped = 0

      for (const doc of localDocs) {
        try {
          await artifactsApi.migrateDoc(accountId, projectId, {
            title: doc.title,
            tiptapContent: doc.content as Record<string, unknown> | null,
            plainText: doc.plainText || '',
            subType: 'Note',
            domain: 'Market',
          })
          migrated++
        } catch (err) {
          console.warn('Failed to migrate doc:', doc.id, err)
          skipped++
        }
      }

      // Clear localStorage after successful migration
      if (migrated > 0 && skipped === 0) {
        localStorage.removeItem(LOCAL_STORAGE_KEY)
      }

      return { migrated, total: localDocs.length, skipped }
    },
    onSuccess: (result) => {
      if (result.migrated > 0) {
        queryClient.invalidateQueries({ queryKey: ['smartDocs'] })
      }
    },
  })
}

/**
 * Check if there are local docs that need migration
 */
export function useHasLocalDocs(): boolean {
  try {
    const stored = localStorage.getItem(LOCAL_STORAGE_KEY)
    if (!stored) return false

    const parsed = JSON.parse(stored)
    const docs = parsed.state?.documents || []
    return docs.length > 0
  } catch {
    return false
  }
}

/**
 * Get count of local docs pending migration
 */
export function useLocalDocsCount(): number {
  try {
    const stored = localStorage.getItem(LOCAL_STORAGE_KEY)
    if (!stored) return 0

    const parsed = JSON.parse(stored)
    const docs = parsed.state?.documents || []
    return docs.length
  } catch {
    return 0
  }
}
